import { useEffect, useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { useSetValidTransportationTypeQuery } from "@/features/BookingWizard/components/WizardStepContainer/steps/AddPlayersStep/hooks/__generated__/useSetValidTransportationTypeQuery.graphql";
import { setValidTransportationTypeQuery } from "@/features/BookingWizard/components/WizardStepContainer/steps/AddPlayersStep/hooks/useSetValidTransportationType";
import { PatronEditBooking } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/types";

export type RoundTransportationType = {
  id: string;
  code?: string | null;
  customCode?: string | null;
  name?: string | null;
  enabled?: boolean | null;
  nineHolePrice?: number | null;
  eighteenHolePrice?: number | null;
};

type Args = {
  slotId?: string | null;
  round: PatronEditBooking.Round | null;
  currentSessionUserClientId: string | null;
};

const getRoundMemberId = (round: PatronEditBooking.Round | null) => {
  const clientType = round?.player.clientType;
  if (clientType === "member" || clientType === "customer") {
    return round?.player.id ?? null;
  }

  if (clientType === "memberHold") {
    return round?.player.id ?? null;
  }

  return null;
};

const getRoundPlayerType = (
  round: PatronEditBooking.Round | null
): "MEMBER" | "GUEST" | "CUSTOMER" => {
  const clientType = round?.player.clientType;
  if (clientType === "member" || clientType === "memberHold") {
    return "MEMBER";
  }
  if (clientType === "customer" || clientType === "unknownCustomer") {
    return "CUSTOMER";
  }
  return "GUEST";
};

export const useRoundTransportationTypes = ({
  slotId,
  round,
  currentSessionUserClientId,
}: Args) => {
  const [queryRef, loadQuery, disposeQuery] =
    useQueryLoader<useSetValidTransportationTypeQuery>(
      setValidTransportationTypeQuery
    );

  const effectiveMemberId = useMemo(() => {
    const roundMemberId = getRoundMemberId(round);
    const clientType = round?.player.clientType;

    if (clientType === "guest" || clientType === "newGuest") {
      return currentSessionUserClientId ?? null;
    }

    return roundMemberId ?? null;
  }, [round, currentSessionUserClientId]);
  const effectivePlayerType = useMemo(() => getRoundPlayerType(round), [round]);

  useEffect(() => {
    if (slotId) {
      loadQuery(
        {
          slotId,
          memberId: effectiveMemberId,
          playerType: effectivePlayerType,
        },
        { fetchPolicy: "store-and-network" }
      );
      return;
    }
    disposeQuery();
  }, [disposeQuery, effectiveMemberId, effectivePlayerType, loadQuery, slotId]);

  return { queryRef };
};

export const useRoundTransportationTypesFromQuery = (
  queryRef: PreloadedQuery<useSetValidTransportationTypeQuery>
) => {
  const data = usePreloadedQuery<useSetValidTransportationTypeQuery>(
    setValidTransportationTypeQuery,
    queryRef
  );

  return (
    data?.node?.transportationTypes?.flatMap((tt) => (tt ? [tt] : [])) ?? []
  );
};
